import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonPinIcon from '@mui/icons-material/PersonPin'
import BusinessIcon from '@mui/icons-material/Business'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import VideoCallIcon from '@mui/icons-material/VideoCall'
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle'
import PaymentIcon from '@mui/icons-material/Payment'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { displayNamesOfEventTypes, GENDER_LIST_ONBOARDING, BOOKING_SESSION_STATUS, BOOKING_SESSION_STATUS_MAP, BOOKING_PAYMENT_STATUSES, SESSION_PLATFORM_ONLINE, SESSION_PLATFORM_OFFLINE, OFFLINE_PAYMENT_METHODS_LIST } from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { getOrdinalSuffixForSuperScript } from 'src/consumer/helpers/utilFunctions'
import { getBookedSlotStripColor } from 'src/clinic/components/ClinicManageBookingsPage/utils/bookedSlotColorUtils'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'

dayjs.extend(utc)

const SUPPORT_PATIENT_HISTORY_EVENT_TYPES = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'therapy-session', label: 'Therapy Session' },
  { value: 'screening', label: 'Screening' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'nutrition-consultation', label: 'Nutrition Consultation' },
  { value: '1on1', label: '1-on-1' },
  { value: 'interview', label: 'Interview' },
  { value: 'webinar', label: 'Webinar' },
]

function getSupportPatientEventTypeColor(eventType) {
  return ({
    consultation: '#6779e3',
    'therapy-session': '#7450aa',
    therapySession: '#7450aa',
    screening: '#f0a6aa',
    assessment: '#875e8a',
    'nutrition-consultation': '#0f766e',
  })[eventType] || '#6779e3'
}

async function fetchSupportPatientVisitHistory({
  patientId,
  clinicId,
  eventTypes = [],
  limit = 100,
}) {
  if (!patientId) return []

  const params = new URLSearchParams()
  params.set('patientId', patientId)
  params.set('sortDescendingOnStartTime', 'true')
  params.set('limit', String(limit))
  if (clinicId) {
    params.set('clinicId', clinicId)
  }
  eventTypes.forEach((eventType) => {
    if (eventType) params.append('eventType', eventType)
  })

  const response = await getCPDataWithoutParams(
    `/api/secure/cc/support/booked-event-history-of-a-patient?${params.toString()}`,
  )

  if (
    response?.status === 200 &&
    isCommonResponseSuccessful(response?.data?.code) &&
    Array.isArray(response?.data?.data?.['booked-event-info'])
  ) {
    return response.data.data['booked-event-info']
      .filter((item) => item?.status === 'booked')
      .sort((a, b) => dayjs.utc(b?.startTime).valueOf() - dayjs.utc(a?.startTime).valueOf())
  }

  return []
}

function StatusChip({ label, sx }) {
  if (!label) return null
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 24,
        fontSize: '11px',
        fontWeight: 600,
        ...sx,
      }}
    />
  )
}

function VisitCard({ visit }) {
  const eventTypeColor = getSupportPatientEventTypeColor(visit.eventType)
  const eventTypeName = displayNamesOfEventTypes[visit.eventType] || visit.eventType || 'Session'
  const visitDate = dayjs.utc(visit.startTime).local()
  const visitEndDate = visit.endTime ? dayjs.utc(visit.endTime).local() : null
  const slotColors = getBookedSlotStripColor(visit, false, true)
  const sessionStatus = visit.sessionStatus
  const sessionStatusLabel = BOOKING_SESSION_STATUS_MAP[sessionStatus]?.title || sessionStatus || 'Unknown'
  const isCompleted = visit.markedCompleted
  const isCancelled = visit.status === 'cancelled'
  const isInProgress = sessionStatus === BOOKING_SESSION_STATUS.IN_PROGRESS
  const isOnline = visit.meetingSrc === SESSION_PLATFORM_ONLINE
  const isOffline = visit.meetingSrc === SESSION_PLATFORM_OFFLINE
  const visitNumber = visit.patientMeetingSeqNumber
  const visitLabel = visitNumber
    ? `${visitNumber}${getOrdinalSuffixForSuperScript(visitNumber)} ${eventTypeName}`
    : eventTypeName
  const patientNumber = visit.patientInfo?.patientNumber || visit.patientNumber
  const patientAge = visit.patientInfo?.dateOfBirth
    ? getAgeFromDobWithTime(visit.patientInfo.dateOfBirth)
    : null
  const patientGender = visit.patientInfo?.gender
  const genderLabel = patientGender
    ? GENDER_LIST_ONBOARDING?.find((g) => g.value === patientGender)?.label || patientGender
    : null
  const paymentStatus = visit.paymentStatus
  const feePaid = visit.feePaid
  const currency = (visit.currency || 'inr').toUpperCase()
  const paymentModeLabel =
    OFFLINE_PAYMENT_METHODS_LIST?.find((mode) => mode.id === visit.feePaymentMode)?.title ||
    visit.feePaymentMode
  const clinicBrandName =
    visit.clinicBrandData?.brandName ||
    visit.clinicBrandData?.title ||
    visit.clinicBrandName
  const clinicLocationName = visit.clinicLocation?.title || visit.clinicName || visit.clinicLocationName

  const sessionStatusStyle = (() => {
    if (isCompleted) return { backgroundColor: '#d1fae5', color: '#047857' }
    if (isCancelled) return { backgroundColor: '#fee2e2', color: '#dc2626' }
    if (isInProgress) return { backgroundColor: '#fef3c7', color: '#92400e' }
    return { backgroundColor: '#e8e9ea', color: '#4b5563' }
  })()

  return (
    <Box sx={{ position: 'relative', mb: 2 }}>
      <Box
        sx={{
          position: 'absolute',
          left: '-20px',
          top: '6px',
          width: 20,
          height: 20,
          borderRadius: '50%',
          backgroundColor: slotColors?.borderColor || eventTypeColor,
          border: '2px solid white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      />
      <Paper
        sx={{
          p: 2,
          borderRadius: 2,
          border: `2px solid ${slotColors?.borderColor || eventTypeColor}40`,
          backgroundColor: slotColors?.background || `${eventTypeColor}05`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', flex: 1 }}>
            <Chip
              label={visitLabel}
              size="medium"
              sx={{
                background: `linear-gradient(135deg, ${eventTypeColor}20 0%, ${eventTypeColor}10 100%)`,
                color: eventTypeColor,
                fontWeight: 700,
                fontSize: '13px',
                height: 28,
                border: `1px solid ${eventTypeColor}40`,
              }}
            />
            <Chip
              icon={isOnline ? <VideoCallIcon sx={{ fontSize: 16 }} /> : <PersonPinCircleIcon sx={{ fontSize: 16 }} />}
              label={isOnline ? 'Video' : 'Offline'}
              size="small"
              sx={{
                height: 24,
                fontSize: '11px',
                fontWeight: 600,
                backgroundColor: isOnline ? 'rgba(99, 102, 241, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                color: isOnline ? '#6366f1' : '#f59e0b',
                '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
              }}
            />
          </Box>
          <StatusChip
            label={isCompleted ? 'Completed' : isCancelled ? 'Cancelled' : sessionStatusLabel}
            sx={sessionStatusStyle}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={600} sx={{ color: slotColors?.fontColor }}>
                {visitDate.format('DD MMM YYYY')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {visitDate.format('h:mm A')}{visitEndDate ? ` - ${visitEndDate.format('h:mm A')}` : ''}
              </Typography>
            </Box>
            {visit.mentorName ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <PersonPinIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {visit.mentorName}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.75 }}>
              {patientNumber ? (
                <Chip label={`#${patientNumber}`} size="small" sx={{ height: 22, fontSize: '11px', fontWeight: 600, backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }} />
              ) : null}
              {patientAge && genderLabel ? (
                <Chip label={`${patientAge}Y / ${genderLabel.charAt(0)}`} size="small" sx={{ height: 22, fontSize: '11px', backgroundColor: 'rgba(236, 72, 153, 0.1)', color: '#ec4899' }} />
              ) : null}
              {visit.appointmentNumber ? (
                <Chip label={`Appt #${visit.appointmentNumber}`} size="small" sx={{ height: 22, fontSize: '11px', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }} />
              ) : null}
            </Box>
            {feePaid ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <PaymentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {currency} {feePaid}
                  {paymentModeLabel ? <span style={{ opacity: 0.7 }}> ({paymentModeLabel})</span> : null}
                </Typography>
                {paymentStatus ? (
                  <Chip
                    label={paymentStatus === BOOKING_PAYMENT_STATUSES?.COMPLETED ? 'Paid' : 'Pending'}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '10px',
                      fontWeight: 600,
                      backgroundColor: paymentStatus === BOOKING_PAYMENT_STATUSES?.COMPLETED ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                      color: paymentStatus === BOOKING_PAYMENT_STATUSES?.COMPLETED ? '#10b981' : '#f59e0b',
                    }}
                  />
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Box>

        {(clinicBrandName || clinicLocationName || (isOffline && visit.chamberRoomName)) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            {clinicBrandName ? (
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {clinicBrandName}
              </Typography>
            ) : null}
            {clinicBrandName && clinicLocationName ? (
              <Typography variant="body2" color="text.disabled">-</Typography>
            ) : null}
            {clinicLocationName ? (
              <Typography variant="body2" color="text.secondary">
                {clinicLocationName}
              </Typography>
            ) : null}
            {isOffline && visit.chamberRoomName ? (
              <>
                <Typography variant="body2" color="text.disabled">-</Typography>
                <MeetingRoomIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">{visit.chamberRoomName}</Typography>
              </>
            ) : null}
          </Box>
        ) : null}
      </Paper>
    </Box>
  )
}

export default function SupportPatientBookingHistory({
  patientId,
  clinicId,
  showFilters = true,
  emptyMessage = 'No visit history found',
}) {
  const [visitHistory, setVisitHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [eventTypes, setEventTypes] = useState([])

  const loadVisitHistory = useCallback(async () => {
    if (!patientId) {
      setVisitHistory([])
      return
    }
    setLoading(true)
    try {
      const bookedEvents = await fetchSupportPatientVisitHistory({
        patientId,
        clinicId,
        eventTypes,
      })
      setVisitHistory(bookedEvents)
    } catch (error) {
      console.error('Error fetching visit history:', error)
      setVisitHistory([])
    } finally {
      setLoading(false)
    }
  }, [clinicId, eventTypes, patientId])

  useEffect(() => {
    loadVisitHistory()
  }, [loadVisitHistory])

  useEffect(() => {
    setEventTypes([])
  }, [patientId, clinicId])

  return (
    <Box>
      {showFilters ? (
        <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Filter by Event Type
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {SUPPORT_PATIENT_HISTORY_EVENT_TYPES.map((option) => {
              const isSelected = eventTypes.includes(option.value)
              const eventColor = getSupportPatientEventTypeColor(option.value)
              return (
                <Chip
                  key={option.value}
                  label={option.label}
                  size="small"
                  onClick={() => {
                    setEventTypes((current) =>
                      isSelected
                        ? current.filter((type) => type !== option.value)
                        : [...current, option.value],
                    )
                  }}
                  sx={{
                    height: 26,
                    fontSize: '11px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    backgroundColor: isSelected ? `${eventColor}20` : 'white',
                    color: isSelected ? eventColor : '#64748b',
                    border: `1.5px solid ${isSelected ? eventColor : '#e2e8f0'}`,
                    '&:hover': {
                      backgroundColor: isSelected ? `${eventColor}30` : '#f1f5f9',
                      borderColor: eventColor,
                    },
                  }}
                />
              )
            })}
            {eventTypes.length > 0 ? (
              <Chip
                label="Clear All"
                size="small"
                onClick={() => setEventTypes([])}
                sx={{
                  height: 26,
                  fontSize: '11px',
                  cursor: 'pointer',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                  border: '1.5px solid #ef4444',
                  '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.2)' },
                }}
              />
            ) : null}
          </Box>
        </Box>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : visitHistory.length > 0 ? (
        <Box sx={{ position: 'relative', pl: 3 }}>
          <Box sx={{ position: 'absolute', left: '11px', top: 0, bottom: 0, width: 2, backgroundColor: 'rgba(103, 121, 227, 0.2)' }} />
          {visitHistory.map((visit, idx) => (
            <VisitCard key={visit.id || idx} visit={visit} />
          ))}
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4, borderRadius: 2, backgroundColor: 'rgba(103, 121, 227, 0.02)', border: '1px dashed rgba(103, 121, 227, 0.2)' }}>
          <HistoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>
        </Box>
      )}
    </Box>
  )
}
